/**
 * New-user help: who gets which first steps.
 *
 * Three kinds, matching the three home pages (App.tsx HomeRouter): a requester
 * lands on MemberHome, a talent on the dashboard, a council member on their
 * focus area. Admins and leadership viewers get none — they land on the
 * cross-FA overview and are not who this is for.
 */
import { isPlainMember } from './userKind'

export type OnboardingKind = 'requester' | 'talent' | 'council'

export function onboardingKind(
  user?: {
    role?: string
    talent_profile_active?: boolean
    views_all_demands?: boolean
  } | null,
): OnboardingKind | null {
  if (!user || user.views_all_demands) return null
  if (isPlainMember(user)) return 'requester'
  if (user.role === 'talent' || user.role === 'member') return 'talent'
  if (user.role === 'council') return 'council'
  return null
}

export interface WelcomeStep {
  title: string
  detail: string
  /** A section anchor on /help. */
  help: string
}

export const WELCOME_STEPS: Record<OnboardingKind, WelcomeStep[]> = {
  requester: [
    { title: 'Submit a demand', detail: 'Say what you need. Two minutes.', help: 'getting-started' },
    { title: 'Track it on Home', detail: 'See where each request stands.', help: 'getting-started' },
    {
      title: 'Want to be staffed?',
      detail: 'Activate your talent profile in My profile.',
      help: 'talent-workflows',
    },
  ],
  talent: [
    { title: 'Add your skills', detail: 'Matching starts from them.', help: 'talent-workflows' },
    { title: 'Browse opportunities', detail: 'Save the ones you like.', help: 'talent-workflows' },
    { title: 'Apply', detail: 'Track every application on Home.', help: 'talent-workflows' },
  ],
  council: [
    {
      title: 'Check new arrivals',
      detail: 'Your home page shows where the app placed them.',
      help: 'council-workflows',
    },
    { title: 'Triage and staff', detail: 'Move demands through their stages.', help: 'council-workflows' },
    { title: 'Post for applications', detail: 'Review who applies.', help: 'council-workflows' },
  ],
}

/** Per user, so a shared browser or a View-as session does not share it. */
function dismissKey(userId: number | string): string {
  return `dac.gettingStartedDismissed.${userId}`
}

export function isChecklistDismissed(userId: number | string | undefined): boolean {
  if (userId == null) return false
  try {
    return window.localStorage.getItem(dismissKey(userId)) === '1'
  } catch {
    return false // storage unavailable — the card shows; harmless.
  }
}

export function dismissChecklist(userId: number | string | undefined): void {
  if (userId == null) return
  try {
    window.localStorage.setItem(dismissKey(userId), '1')
  } catch {
    // storage unavailable — the card may come back next load; harmless.
  }
}
